import React, { useMemo } from 'react';

import { isEqual } from 'lodash/fp';

import { Currency } from '@/constants';
import useChainByCurrency, { type ChainData } from '@/hooks/useChainByCurrency';

import BuildDetailsTokenInfo from '../BuildDetailsTokenInfo';
import BuildDetailsGrid from '../components/BuildDetailsGrid';

import { BuildDetailsChainTokenInfoActions } from './BuildDetailsChainTokenInfoActions';

interface BuildDetailsChainTokenInfoProps {
  chain: ChainData;
}

export const BuildDetailsChainTokenInfo: React.FC<BuildDetailsChainTokenInfoProps> = ({ chain }) => {
  const { findChainByCurrency } = useChainByCurrency();

  const tokensByChain = useMemo(() => {
    return Object.values(Currency)
      .filter((currency) => {
        const chainByCurrency = findChainByCurrency(currency);

        if (!chainByCurrency?.chainData) {
          return false;
        }

        return isEqual(chainByCurrency.chainData, chain.chainData);
      })
      .sort(([keyA], [keyB]) => {
        const chainA = findChainByCurrency(keyA as Currency);
        const chainB = findChainByCurrency(keyB as Currency);

        // Sort native tokens to the top
        if (chainA?.isNative && !chainB?.isNative) return -1;
        if (!chainA?.isNative && chainB?.isNative) return 1;
        return keyA.localeCompare(keyB);
      });
  }, [findChainByCurrency]);

  return (
    <BuildDetailsGrid
      collapsible
      className="grid-cols-[18px_1fr] py-2"
      contentClassName="m-0.5 p-0.5 bg-background/50 rounded-xl"
      content={
        <div className="flex flex-col gap-0.5">
          {tokensByChain.map((currency) => {
            return <BuildDetailsTokenInfo key={currency} currency={currency} />;
          })}
        </div>
      }
    >
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <div className="bg-foreground rounded-full p-0.5">
            <img src={(chain.chainData?.custom?.icon as string) || ''} alt={chain.chainData?.name} className="size-6" />
          </div>
          {chain.chainData?.name}
        </div>

        <div className="flex items-center gap-1">
          <BuildDetailsChainTokenInfoActions tokensByChain={tokensByChain} />
        </div>
      </div>
    </BuildDetailsGrid>
  );
};
